import { TZDate } from '@date-fns/tz'
import { format, isValid } from 'date-fns'

import type { SupportedTimezones } from '@/payload-types'

export const DEFAULT_EVENT_TIMEZONE: SupportedTimezones = 'America/New_York'

/** Common event timezones; values must match Payload `SupportedTimezones`. */
export const CALENDAR_TIMEZONE_OPTIONS: {
  value: SupportedTimezones
  label: string
  popular?: boolean
}[] = [
  { value: 'America/New_York', label: 'Eastern (ET)', popular: true },
  { value: 'America/Chicago', label: 'Central (CT)', popular: true },
  { value: 'America/Denver', label: 'Mountain (MT)', popular: true },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)', popular: true },
  { value: 'America/Phoenix', label: 'Arizona', popular: true },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
  { value: 'Europe/London', label: 'London', popular: true },
  { value: 'Asia/Singapore', label: 'Singapore', popular: true },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Calcutta', label: 'India' },
  { value: 'Australia/Sydney', label: 'Sydney' },
]

export function calendarDayParts(day: Date) {
  return {
    year: day.getFullYear(),
    month: day.getMonth(),
    day: day.getDate(),
  }
}

/** Wall-clock date/time in `timeZone` → UTC ISO string for Payload. */
export function buildZonedISO(day: Date, time: string | undefined, timeZone: string): string {
  const { year, month, day: date } = calendarDayParts(day)
  const trimmed = time?.trim()

  if (!trimmed) {
    return new TZDate(year, month, date, 0, 0, 0, timeZone).toISOString()
  }

  const [hours, minutes] = trimmed.split(':').map((part) => parseInt(part, 10))
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return new TZDate(year, month, date, 0, 0, 0, timeZone).toISOString()
  }

  return new TZDate(year, month, date, hours, minutes, 0, timeZone).toISOString()
}

export function parseZonedISO(iso: string | undefined, timeZone: string): TZDate | undefined {
  if (!iso) return undefined
  const tzDate = TZDate.tz(timeZone, iso)
  return isValid(tzDate) ? tzDate : undefined
}

/** Calendar `selected` prop from a zoned instant. */
export function zonedDateToCalendarDate(tzDate: TZDate): Date {
  return new Date(tzDate.getFullYear(), tzDate.getMonth(), tzDate.getDate())
}

export function hasWallClockTime(tzDate: TZDate): boolean {
  return tzDate.getHours() !== 0 || tzDate.getMinutes() !== 0 || tzDate.getSeconds() !== 0
}

export function formatZonedDisplay(
  tzDate: TZDate,
  time?: string,
  withTimePicker?: boolean,
): string {
  if (withTimePicker && time?.trim()) {
    const { year, month, day } = calendarDayParts(zonedDateToCalendarDate(tzDate))
    const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10))
    const display = new TZDate(year, month, day, hours, minutes, 0, tzDate.timeZone)
    return format(display, 'MM/dd/yyyy h:mm a')
  }
  return format(tzDate, 'MM/dd/yyyy')
}

/** Format IANA timezone as `GMT±HH:MM`. */
export function formatGmtOffset(timeZone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(at)
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value
    if (offset) {
      return offset.replace(/^GMT([+-])(\d):/, 'GMT$10$2:').replace(/^UTC/, 'GMT')
    }
  } catch {
    // fall through
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(at)
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value
    if (offset) {
      const normalized = offset.replace(/^UTC/, 'GMT')
      const match = normalized.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/)
      if (match) {
        const sign = match[1]
        const hours = match[2].padStart(2, '0')
        const minutes = match[3] ?? '00'
        return `GMT${sign}${hours}:${minutes}`
      }
      return normalized
    }
  } catch {
    // fall through
  }

  return 'GMT'
}

export function timezoneCityLabel(timeZone: string): string {
  const option = CALENDAR_TIMEZONE_OPTIONS.find((tz) => tz.value === timeZone)
  if (option) return option.label
  return timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone
}

export function generateTimeSlots(intervalMinutes = 30): { value: string; label: string }[] {
  const slots: { value: string; label: string }[] = []
  for (let minutes = 0; minutes < 24 * 60; minutes += intervalMinutes) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    const value = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
    const labelDate = new Date(2000, 0, 1, hours, mins)
    slots.push({ value, label: format(labelDate, 'h:mm a') })
  }
  return slots
}

/** Next half-hour wall-clock `HH:mm` in the given timezone (from "now"). */
export function nextHalfHourTime(timeZone: string, at: Date = new Date()): string {
  const now = TZDate.tz(timeZone, at)
  let hours = now.getHours()
  let minutes = now.getMinutes()
  if (minutes === 0) {
    // already on the hour — use current hour
  } else if (minutes <= 30) {
    minutes = 30
  } else {
    hours = (hours + 1) % 24
    minutes = 0
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/** Add minutes to an `HH:mm` string; returns `{ time, dayOffset }`. */
export function addMinutesToTime(
  time: string,
  addMinutes: number,
): { time: string; dayOffset: number } {
  const [h, m] = time.split(':').map((part) => parseInt(part, 10))
  const total = (h || 0) * 60 + (m || 0) + addMinutes
  const dayOffset = Math.floor(total / (24 * 60))
  const normalized = ((total % (24 * 60)) + 24 * 60) % (24 * 60)
  const hours = Math.floor(normalized / 60)
  const minutes = normalized % 60
  return {
    time: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    dayOffset,
  }
}

export type TimeParts12 = {
  hour: string
  minute: string
  period: 'AM' | 'PM'
}

/** Format `HH:mm` as `h:mm a` (e.g. `2:00 PM`). */
export function formatTimePill(time: string | undefined): string {
  if (!time?.trim()) return ''
  const parts = parseTimeTo12Hour(time)
  if (!parts) return time
  const hour = String(parseInt(parts.hour, 10))
  return `${hour}:${parts.minute} ${parts.period}`
}

export function parseTimeTo12Hour(time: string | undefined): TimeParts12 | null {
  if (!time?.trim()) return null
  const [hRaw, mRaw] = time.split(':')
  const hours24 = parseInt(hRaw ?? '', 10)
  const minutes = parseInt(mRaw ?? '', 10)
  if (Number.isNaN(hours24) || Number.isNaN(minutes)) return null
  const period: 'AM' | 'PM' = hours24 >= 12 ? 'PM' : 'AM'
  let hour12 = hours24 % 12
  if (hour12 === 0) hour12 = 12
  return {
    hour: String(hour12),
    minute: String(minutes).padStart(2, '0'),
    period,
  }
}

/** Compose 12-hour parts into `HH:mm`. */
export function composeTimeFrom12Hour(parts: {
  hour: string | number
  minute: string | number
  period: 'AM' | 'PM'
}): string | null {
  let hour12 = typeof parts.hour === 'number' ? parts.hour : parseInt(parts.hour, 10)
  let minute = typeof parts.minute === 'number' ? parts.minute : parseInt(parts.minute, 10)
  if (Number.isNaN(hour12) || Number.isNaN(minute)) return null
  hour12 = Math.min(12, Math.max(1, hour12))
  minute = Math.min(59, Math.max(0, minute))
  let hours24 = hour12 % 12
  if (parts.period === 'PM') hours24 += 12
  return `${String(hours24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}
